import React, { useEffect, useMemo, useState } from "react";
import jwt_decode from "jwt-decode";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getVentas } from "../helpers/rutaVentas";
import { getPagosVenta } from "../helpers/rutaPagoVentas";
import { getFinancieras } from "../helpers/rutaFinancieras";

import logo from "../images/ridersmiami-logo.png";

const todayLocalISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const clean = (v) => String(v ?? "").trim();
const getRoleUsuario = () => {
  const token =
    localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    return (
      decoded?.usuario?.role ||
      decoded?.user?.role ||
      decoded?.role ||
      ""
    );
  } catch {
    return "";
  }
};

const getSedeUsuario = () => {
  const token =
    localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    const usuario =
      decoded?.usuario ||
      decoded?.user ||
      decoded ||
      {};

    return clean(usuario?.sede)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toUpperCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

const esVentaMiamiPropia = (
  venta = {}
) => {
  const sede = clean(venta?.sede)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sede === "MIAMI";
};

const filtrarVentasReporte = (
  ventas = []
) => {
  const role = getRoleUsuario();
  const sede = getSedeUsuario();

  const esManagerMiami =
    role === "MANAGER_ROLE" &&
    sede === "MIAMI";

  if (!esManagerMiami) {
    return ventas;
  }

  return ventas.filter(
    (venta) =>
      esVentaMiamiPropia(venta)
  );
};

const normalizarTexto = (value) => String(value || "").toUpperCase();

const formatDate = (value) => {
  if (!value) return "";

  // Si viene de Mongo como ISO: 2026-06-01T00:00:00.000Z
  // tomamos la parte textual sin moverla por zona horaria.
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatNum = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const nombreClienteCompra = (cliente) => {
  if (!cliente) return "";

  return (
    cliente.razonsocial ||
    [cliente.apellido1, cliente.apellido2, cliente.nombre]
      .filter(Boolean)
      .join(" ") ||
    ""
  );
};

const nombreUsuario = (usuario) => {
  if (!usuario) return "";

  if (typeof usuario === "string") {
    return "";
  }

  return (
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ") ||
    usuario.email ||
    ""
  );
};

const nombreVendedorVenta = (venta) => {
  return (
    venta?.vendedorNombre ||
    venta?.usuarioNombre ||
    nombreUsuario(venta?.vendedor) ||
    nombreUsuario(venta?.usuario) ||
    ""
  );
};

const nombreFinanciera = (financiera) => {
  if (!financiera) return "";

  if (typeof financiera === "string") {
    return financiera;
  }

  return financiera.financiera || financiera.razonsocial || "";
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const estadoPagoBadge = (estado) => {
  const e = clean(estado).toUpperCase();

  if (e === "PAGADO") return "badge bg-success";
  if (e === "PARCIAL") return "badge bg-warning text-dark";
  if (e === "PENDIENTE") return "badge bg-danger";

  return "badge bg-secondary";
};

const estadoAcreditacionBadge = (estado) => {
  const e = clean(estado).toUpperCase();

  if (e === "ACREDITADO") return "badge bg-success";
  if (e === "PENDIENTE") return "badge bg-warning text-dark";
  if (e === "ANULADO") return "badge bg-danger";

  return "badge bg-secondary";
};

const esPagoCreditoFinanciera = (fila = {}) =>
  clean(fila.medioPago).toUpperCase() === "CREDITO FINANCIERA";

const contratoFirmadoTexto = (fila = {}) => {
  if (!fila.pagoId) return "-";
  if (!esPagoCreditoFinanciera(fila)) return "-";

  return fila.contratoFirmado ? "FIRMADO" : "NO";
};

const contratoFirmadoBadge = (fila = {}) => {
  if (!fila.pagoId || !esPagoCreditoFinanciera(fila)) {
    return "fw-bold text-muted";
  }

  return fila.contratoFirmado
    ? "fw-bold text-success"
    : "fw-bold text-danger";
};

const procesarEnLotes = async (items, size, fn) => {
  const resultados = [];

  for (let i = 0; i < items.length; i += size) {
    const lote = items.slice(i, i + size);
    const res = await Promise.all(lote.map(fn));
    resultados.push(...res);
  }

  return resultados;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const TAX_PORCENTAJE_DEFAULT = 7;

const valorDefinido = (...valores) =>
  valores.find((valor) => valor !== undefined && valor !== null && valor !== "");

const calcularDatosTax = ({ ventaPago = {}, venta = {}, resumenPago = {}, totalVenta = 0 }) => {
  const taxPorcentaje = Number(
    valorDefinido(
      resumenPago.taxPorcentaje,
      ventaPago.taxPorcentaje,
      venta.taxPorcentaje,
      TAX_PORCENTAJE_DEFAULT
    )
  );

  const taxGuardado = valorDefinido(
    resumenPago.taxMontoUSD,
    ventaPago.taxMontoUSD,
    venta.taxMontoUSD
  );

  const sinTaxGuardado = valorDefinido(
    resumenPago.totalSinTaxUSD,
    ventaPago.totalSinTaxUSD,
    venta.totalSinTaxUSD
  );

  const taxMontoUSD =
    taxGuardado !== undefined
      ? round2(Number(taxGuardado || 0))
      : round2(Number(totalVenta || 0) * (taxPorcentaje / 100));

  const totalSinTaxUSD =
    sinTaxGuardado !== undefined
      ? round2(Number(sinTaxGuardado || 0))
      : round2(Number(totalVenta || 0) - taxMontoUSD);

  return {
    taxPorcentaje,
    taxMontoUSD,
    totalSinTaxUSD,
  };
};

export default function ReporteFinancieroVentas() {
  const [desde, setDesde] = useState(firstDayMonthISO());
  const [hasta, setHasta] = useState(todayLocalISO());

  const [q, setQ] = useState("");
  const [estadoVenta, setEstadoVenta] = useState("");
  const [estadoPago, setEstadoPago] = useState("");
  const [medioPago, setMedioPago] = useState("");
  const [estadoAcreditacion, setEstadoAcreditacion] = useState("");
  const [financieraFiltro, setFinancieraFiltro] = useState("");
  const [vendedor, setVendedor] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filas, setFilas] = useState([]);
  const [financieras, setFinancieras] = useState([]);

  const [totales, setTotales] = useState({
    cantidadVentas: 0,
    cantidadPagos: 0,
    totalVentasUSD: 0,
    totalTaxUSD: 0,
    totalSinTaxUSD: 0,
    totalCargadoUSD: 0,
    totalFeeUSD: 0,
    totalCobradoClienteUSD: 0,
    totalMontoFinancieraUSD: 0,
    totalAcreditadoUSD: 0,
    totalPendienteAcreditarUSD: 0,
    saldoPendienteUSD: 0,
  });

  const cargarReporte = async () => {
    setLoading(true);
    setError("");

    try {
      const LIMIT = 200;
      let page = 1;
      let total = 0;
      let ventas = [];

      do {
        const resp = await getVentas({
          page,
          limit: LIMIT,
          q: "",
          estado: estadoVenta,
          desde,
          hasta,
          vendedor,
        });

        const lote = Array.isArray(resp?.ventas) ? resp.ventas : [];

        ventas = ventas.concat(lote);
        total = Number(resp?.total || lote.length || 0);
        page += 1;
      } while (ventas.length < total);

      ventas =
        filtrarVentasReporte(
          ventas
        );

      const respFinancieras = await getFinancieras({
        page: 1,
        limit: 500,
      });

      setFinancieras(
        Array.isArray(respFinancieras?.financieras)
          ? respFinancieras.financieras
          : []
      );

      const filasPorVenta = await procesarEnLotes(ventas, 8, async (venta) => {
        const ventaId = venta._id || venta.id;

        if (!ventaId) return [];

        let respPagos = null;

        try {
          respPagos = await getPagosVenta(ventaId);
        } catch (e) {
          console.warn("No se pudieron leer pagos de venta:", venta.nroventa, e);
        }

        const pagos = Array.isArray(respPagos?.pagos) ? respPagos.pagos : [];
        const ventaPago = respPagos?.venta || venta;
        const resumenPago = respPagos?.resumenPago || {};

        const totalVenta = Number(
          resumenPago.totalVenta ?? ventaPago.totalUSD ?? venta.totalUSD ?? 0
        );

        const totalPagadoUSD = Number(
          resumenPago.totalPagadoUSD ??
            ventaPago.totalPagadoUSD ??
            venta.totalPagadoUSD ??
            0
        );

        const totalAcreditadoUSD = Number(
          resumenPago.totalAcreditadoUSD ??
            ventaPago.totalAcreditadoUSD ??
            venta.totalAcreditadoUSD ??
            0
        );

        const saldoPendienteUSD =
          resumenPago.saldoPendienteUSD !== undefined &&
          resumenPago.saldoPendienteUSD !== null
            ? Number(resumenPago.saldoPendienteUSD || 0)
            : Math.max(totalVenta - totalAcreditadoUSD, 0);

        const estadoPagoVenta =
          resumenPago.estadoPago || ventaPago.estadoPago || "PENDIENTE";

        const { taxPorcentaje, taxMontoUSD, totalSinTaxUSD } = calcularDatosTax({
          ventaPago,
          venta,
          resumenPago,
          totalVenta,
        });

        const base = {
          ventaId,
          nroventa: ventaPago.nroventa || venta.nroventa,
          fechaVenta: ventaPago.fecha || venta.fecha,
          clienteCompra: ventaPago.clienteCompra || venta.clienteCompra,
          vendedorObj: ventaPago.vendedor || venta.vendedor,
          usuarioObj: ventaPago.usuario || venta.usuario,
          vendedorNombre: nombreVendedorVenta(ventaPago || venta),
          estadoVenta: ventaPago.estado || venta.estado,
          estadoPago: estadoPagoVenta,
          totalVentaUSD: totalVenta,
          taxPorcentaje,
          taxMontoUSD,
          totalSinTaxUSD,
          totalPagadoUSD,
          totalAcreditadoUSD,
          saldoPendienteUSD,
        };

        if (pagos.length === 0) {
          return [
            {
              ...base,
              pagoId: "",
              nropago: "",
              fechaPago: "",
              medioPago: "SIN PAGOS",
              financiera: null,
              financieraNombre: "",
              montoUSD: 0,
              cuotasFinanciacion: 0,
              feePorcentaje: 0,
              feeMontoUSD: 0,
              totalCobrarClienteUSD: 0,
              montoFinancieraUSD: 0,
              estadoAcreditacion: "PENDIENTE",
              fechaAcreditacion: "",
              observacionesPago: "",
              contratoFirmado: false,
            },
          ];
        }

        return pagos.map((pago) => ({
          ...base,
          pagoId: pago._id || pago.id,
          nropago: pago.nropago,
          fechaPago: pago.fechaPago,
          medioPago: pago.medioPago,
          financiera: pago.financiera,
          financieraNombre: nombreFinanciera(pago.financiera),
          montoUSD: Number(pago.montoUSD || 0),
          cuotasFinanciacion: Number(pago.cuotasFinanciacion || 0),
          feePorcentaje: Number(pago.feePorcentaje || 0),

          feeMontoUSD: Number(
            pago.feeMontoUSD || 0
          ),

          /*
            Total bruto que efectivamente pagó el cliente.
            Para pagos anteriores que no lo tengan guardado,
            se reconstruye como monto aplicado + fee.
          */
          totalCobrarClienteUSD: Number(
            pago.totalCobrarClienteUSD ??
              (
                Number(pago.montoUSD || 0) +
                Number(pago.feeMontoUSD || 0)
              )
          ),

          montoFinancieraUSD: Number(
            pago.montoFinancieraUSD || 0
          ),
          estadoAcreditacion: pago.estadoAcreditacion,
          fechaAcreditacion: pago.fechaAcreditacion,
          contratoFirmado: Boolean(pago.contratoFirmado),
          observacionesPago: pago.observaciones,
        }));
      });

      const filasArmadas = filasPorVenta.flat();

      setFilas(filasArmadas);
    } catch (err) {
      console.error("Error reporte financiero ventas:", err);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar el reporte financiero"
      );

      setFilas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vendedoresDisponibles = useMemo(() => {
    const map = new Map();

    filas.forEach((fila) => {
      const id = getId(fila.vendedorObj) || getId(fila.usuarioObj);
      const nombre = fila.vendedorNombre || nombreUsuario(fila.vendedorObj);

      if (id && nombre && !map.has(id)) {
        map.set(id, nombre);
      }
    });

    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [filas]);

  const filasFiltradas = useMemo(() => {
    const texto = clean(q).toUpperCase();

    return filas.filter((fila) => {
      if (estadoPago && fila.estadoPago !== estadoPago) return false;
      if (medioPago && fila.medioPago !== medioPago) return false;

      if (
        estadoAcreditacion &&
        fila.estadoAcreditacion !== estadoAcreditacion
      ) {
        return false;
      }

      if (financieraFiltro) {
        const idFin = getId(fila.financiera);
        if (idFin !== financieraFiltro) return false;
      }

      if (texto) {
        const cadena = [
          fila.nroventa,
          nombreClienteCompra(fila.clienteCompra),
          fila.vendedorNombre,
          fila.estadoVenta,
          fila.estadoPago,
          fila.medioPago,
          fila.financieraNombre,
          fila.estadoAcreditacion,
          contratoFirmadoTexto(fila),
          fila.observacionesPago,
        ]
          .join(" ")
          .toUpperCase();

        if (!cadena.includes(texto)) return false;
      }

      return true;
    });
  }, [
    filas,
    q,
    estadoPago,
    medioPago,
    estadoAcreditacion,
    financieraFiltro,
  ]);

  useEffect(() => {
    const ventasMap = new Map();

    filasFiltradas.forEach((fila) => {
      if (!ventasMap.has(fila.ventaId)) {
        ventasMap.set(fila.ventaId, fila);
      }
    });

    const ventasUnicas = Array.from(ventasMap.values());

    const totalVentasUSD = ventasUnicas.reduce(
      (acc, fila) => acc + Number(fila.totalVentaUSD || 0),
      0
    );

    const totalTaxUSD = ventasUnicas.reduce(
      (acc, fila) => acc + Number(fila.taxMontoUSD || 0),
      0
    );

    const totalSinTaxUSD = ventasUnicas.reduce(
      (acc, fila) => acc + Number(fila.totalSinTaxUSD || 0),
      0
    );

    const saldoPendienteUSD = ventasUnicas.reduce(
      (acc, fila) => acc + Number(fila.saldoPendienteUSD || 0),
      0
    );

    const pagosReales = filasFiltradas.filter((f) => f.pagoId);

    const totalCargadoUSD = pagosReales.reduce(
      (acc, fila) => acc + Number(fila.montoUSD || 0),
      0
    );

    const pagosFinanciera = pagosReales.filter(esPagoCreditoFinanciera);

    const totalFeeUSD = pagosFinanciera.reduce(
      (acc, fila) => acc + Number(fila.feeMontoUSD || 0),
      0
    );

    const totalCobradoClienteUSD = pagosReales.reduce(
      (acc, fila) =>
        acc + Number(fila.totalCobrarClienteUSD || 0),
      0
    );

    const totalMontoFinancieraUSD = pagosFinanciera.reduce(
      (acc, fila) => acc + Number(fila.montoFinancieraUSD || 0),
      0
    );

    const totalAcreditadoUSD = pagosReales
      .filter((f) => f.estadoAcreditacion === "ACREDITADO")
      .reduce((acc, fila) => acc + Number(fila.montoUSD || 0), 0);

    const totalPendienteAcreditarUSD = pagosReales
      .filter((f) => f.estadoAcreditacion === "PENDIENTE")
      .reduce((acc, fila) => acc + Number(fila.montoUSD || 0), 0);

    setTotales({
      cantidadVentas: ventasUnicas.length,
      cantidadPagos: pagosReales.length,
      totalVentasUSD: round2(totalVentasUSD),
      totalTaxUSD: round2(totalTaxUSD),
      totalSinTaxUSD: round2(totalSinTaxUSD),
      totalFeeUSD: round2(totalFeeUSD),

      totalCobradoClienteUSD: round2(
        totalCobradoClienteUSD
      ),

      totalMontoFinancieraUSD: round2(
        totalMontoFinancieraUSD
      ),
      totalCargadoUSD: round2(totalCargadoUSD),
      totalAcreditadoUSD: round2(totalAcreditadoUSD),
      totalPendienteAcreditarUSD: round2(totalPendienteAcreditarUSD),
      saldoPendienteUSD: round2(saldoPendienteUSD),
    });
  }, [filasFiltradas]);

  const limpiarFiltros = () => {
    setDesde(firstDayMonthISO());
    setHasta(todayLocalISO());
    setQ("");
    setEstadoVenta("");
    setEstadoPago("");
    setMedioPago("");
    setEstadoAcreditacion("");
    setFinancieraFiltro("");
    setVendedor("");
  };

  const generarPDF = async () => {
    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      const logoImg = await cargarImagen(logo);

      doc.addImage(logoImg, "PNG", 10, 8, 34, 12);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Reporte Financiero de Ventas", 50, 14);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Riders Miami", 50, 20);

      doc.text(`Desde: ${desde || "-"}   Hasta: ${hasta || "-"}`, 220, 14, {
        align: "right",
      });

      doc.text(
        `Estado venta: ${estadoVenta || "Todos"} | Estado pago: ${
          estadoPago || "Todos"
        } | Medio: ${medioPago || "Todos"} | Acreditación: ${
          estadoAcreditacion || "Todos"
        }`,
        287,
        20,
        { align: "right" }
      );

      doc.setDrawColor(180);
      doc.line(10, 24, 287, 24);

      const resumenY = 28;
      const boxW = 44;
      const boxH = 12;
      const gapX = 2;
      const gapY = 2;
      const columnasResumen = 6;

      const resumen = [
        { label: "Ventas", value: String(totales?.cantidadVentas || 0) },
        { label: "Pagos", value: String(totales?.cantidadPagos || 0) },
        {
          label: "Venta total",
          value: formatUSDPdf(totales?.totalVentasUSD || 0),
        },
        {
          label: "Tax",
          value: formatUSDPdf(totales?.totalTaxUSD || 0),
        },
        {
          label: "Venta sin tax",
          value: formatUSDPdf(totales?.totalSinTaxUSD || 0),
        },
        {
          label: "Cargado",
          value: formatUSDPdf(totales?.totalCargadoUSD || 0),
        },
        {
          label: "Fee",
          value: formatUSDPdf(totales?.totalFeeUSD || 0),
        },
        {
          label: "Cobrado cliente",
          value: formatUSDPdf(
            totales?.totalCobradoClienteUSD || 0
          ),
        },
        {
          label: "Monto financ.",
          value: formatUSDPdf(totales?.totalMontoFinancieraUSD || 0),
        },
        {
          label: "Acreditado",
          value: formatUSDPdf(totales?.totalAcreditadoUSD || 0),
        },
        {
          label: "Pend. acredit.",
          value: formatUSDPdf(totales?.totalPendienteAcreditarUSD || 0),
        },
        {
          label: "Saldo",
          value: formatUSDPdf(totales?.saldoPendienteUSD || 0),
        },
      ];

      resumen.forEach((item, i) => {
        const columna = i % columnasResumen;
        const filaResumen = Math.floor(i / columnasResumen);
        const x = 7 + columna * (boxW + gapX);
        const y = resumenY + filaResumen * (boxH + gapY);

        doc.setDrawColor(210);
        doc.roundedRect(x, y, boxW, boxH, 1.5, 1.5);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.4);
        doc.text(item.label, x + 1.5, y + 4);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.7);
        doc.text(String(item.value), x + 1.5, y + 9);
      });

      const body = filasFiltradas.map((fila) => [
        formatDate(fila.fechaVenta),
        fila.nroventa || "",
        normalizarTexto(nombreClienteCompra(fila.clienteCompra)),
        normalizarTexto(fila.vendedorNombre),
        fila.estadoVenta || "",
        fila.estadoPago || "",

        // Bloque de venta
        formatUSDPdf(fila.totalVentaUSD),
        formatUSDPdf(fila.taxMontoUSD),
        formatUSDPdf(fila.totalSinTaxUSD),
        formatUSDPdf(fila.saldoPendienteUSD),

        // Bloque de pagos
        formatDate(fila.fechaPago),
        fila.medioPago || "",
        fila.financieraNombre || "",
        formatUSDPdf(fila.montoUSD),

        esPagoCreditoFinanciera(fila)
          ? `${formatNum(fila.feePorcentaje)}%`
          : "-",

        esPagoCreditoFinanciera(fila)
          ? formatUSDPdf(fila.feeMontoUSD)
          : "-",

        formatUSDPdf(
          fila.totalCobrarClienteUSD
        ),

        esPagoCreditoFinanciera(fila)
          ? formatUSDPdf(fila.montoFinancieraUSD)
          : "-",

        fila.estadoAcreditacion || "",
        contratoFirmadoTexto(fila),
        formatDate(fila.fechaAcreditacion),
      ]);

      autoTable(doc, {
        startY: 59,
        theme: "grid",

        margin: {
          left: 6,
          right: 6,
          bottom: 10,
        },

        head: [
          [
            "F. venta",
            "Venta",
            "Cliente",
            "Vendedor",
            "Est. venta",
            "Est. pago",

            "Venta total",
            "Tax",
            "Venta s/tax",
            "Saldo",

            "F. pago",
            "Medio",
            "Financ.",
            "Mto aplicado",
            "Fee %",
            "Fee US$",
            "Total cliente",
            "Mto financ.",
            "Acred.",
            "Firm.",
            "F. acred.",
          ],
        ],

        body,

        foot: [
          [
            "Totales",
            "",
            "",
            "",
            "",
            "",

            formatUSDPdf(totales?.totalVentasUSD || 0),
            formatUSDPdf(totales?.totalTaxUSD || 0),
            formatUSDPdf(totales?.totalSinTaxUSD || 0),
            formatUSDPdf(totales?.saldoPendienteUSD || 0),

            "",
            "",
            "",

            formatUSDPdf(totales?.totalCargadoUSD || 0),

            "",

            formatUSDPdf(totales?.totalFeeUSD || 0),
            formatUSDPdf(
              totales?.totalCobradoClienteUSD || 0
            ),
            formatUSDPdf(totales?.totalMontoFinancieraUSD || 0),
            formatUSDPdf(totales?.totalAcreditadoUSD || 0),

            "",
            "",
          ],
        ],

        showHead: "everyPage",
        showFoot: "lastPage",

        styles: {
          font: "helvetica",
          fontSize: 5.4,
          cellPadding: 0.55,
          overflow: "linebreak",
          valign: "middle",
          minCellHeight: 4.8,
          lineColor: [205, 210, 215],
          lineWidth: 0.15,
        },

        alternateRowStyles: {
          fillColor: [248, 249, 250],
        },

        headStyles: {
          fillColor: [33, 37, 41],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 5.3,
          halign: "center",
          valign: "middle",
          cellPadding: 0.7,
          minCellHeight: 7,
        },

        footStyles: {
          fillColor: [238, 242, 247],
          textColor: [20, 20, 20],
          fontStyle: "bold",
          fontSize: 5.2,
          halign: "right",
          cellPadding: 0.65,
        },

        columnStyles: {
          0: {
            cellWidth: 13,
            halign: "center",
          }, // Fecha venta

          1: {
            cellWidth: 7,
            halign: "center",
          }, // Venta

          2: {
            cellWidth: 29,
            halign: "left",
          }, // Cliente

          3: {
            cellWidth: 26,
            halign: "left",
          }, // Vendedor

          4: {
            cellWidth: 14,
            halign: "center",
          }, // Estado venta

          5: {
            cellWidth: 13,
            halign: "center",
          }, // Estado pago

          6: {
            cellWidth: 14,
            halign: "right",
          }, // Venta total

          7: {
            cellWidth: 12,
            halign: "right",
          }, // Tax

          8: {
            cellWidth: 15,
            halign: "right",
          }, // Venta sin tax

          9: {
            cellWidth: 13,
            halign: "right",
          }, // Saldo

          10: {
            cellWidth: 13,
            halign: "center",
          }, // Fecha pago

          11: {
            cellWidth: 19,
            halign: "left",
          }, // Medio

          12: {
            cellWidth: 11,
            halign: "left",
          }, // Financiera

          13: {
            cellWidth: 14,
            halign: "right",
          }, // Monto

          14: {
            cellWidth: 7,
            halign: "right",
          }, // Fee porcentaje

          15: {
            cellWidth: 12,
            halign: "right",
          }, // Fee US$

          16: {
            cellWidth: 14,
            halign: "right",
          }, // Total cliente

          17: {
            cellWidth: 14,
            halign: "right",
          }, // Monto financiera

          18: {
            cellWidth: 16,
            halign: "center",
          }, // Acreditación

          19: {
            cellWidth: 9,
            halign: "center",
          }, // Firmado

          20: {
            cellWidth: 13,
            halign: "center",
          }, // Fecha acreditación
        },

        didParseCell: (data) => {
          if (
            data.section !== "body" &&
            data.section !== "foot"
          ) {
            return;
          }

          // Venta total
          if (data.column.index === 6) {
            data.cell.styles.fontStyle = "bold";
          }

          // Tax
          if (data.column.index === 7) {
            data.cell.styles.textColor = [13, 110, 253];
            data.cell.styles.fontStyle = "bold";
          }

          // Venta sin tax
          if (data.column.index === 8) {
            data.cell.styles.textColor = [13, 110, 253];
            data.cell.styles.fontStyle = "bold";
          }

          // Saldo
          if (data.column.index === 9) {
            data.cell.styles.fontStyle = "bold";
          }

          // Monto cargado
          if (data.column.index === 13) {
            data.cell.styles.fontStyle = "bold";
          }

          // Fee retenido
          if (data.column.index === 15) {
            data.cell.styles.textColor = [220, 53, 69];
            data.cell.styles.fontStyle = "bold";
          }
          // Total cobrado al cliente
          if (data.column.index === 16) {
            data.cell.styles.textColor = [13, 110, 253];
            data.cell.styles.fontStyle = "bold";
          }

          // Monto financiera
          if (data.column.index === 17) {
            data.cell.styles.textColor = [25, 135, 84];
            data.cell.styles.fontStyle = "bold";
          }

          // Firmado
          if (
            data.section === "body" &&
            data.column.index === 19
          ) {
            const value = String(
              data.cell.raw || ""
            ).toUpperCase();

            if (value === "FIRMADO") {
              data.cell.styles.textColor = [25, 135, 84];
              data.cell.styles.fontStyle = "bold";
            }

            if (value === "NO") {
              data.cell.styles.textColor = [220, 53, 69];
              data.cell.styles.fontStyle = "bold";
            }
          }
         
        },

        didDrawPage: (data) => {
          const pageHeight =
            doc.internal.pageSize.getHeight();

          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(110);

          doc.text(
            `Página ${data.pageNumber}`,
            287,
            pageHeight - 4,
            {
              align: "right",
            }
          );
        },
      });


      doc.save(
        `reporte-financiero-ventas-${desde || "desde"}-${hasta || "hasta"}.pdf`
      );
    } catch (error) {
      console.error("Error generando PDF:", error);
      alert("No se pudo generar el PDF.");
    }
  };

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">Reporte Financiero de Ventas</h4>
              <small className="text-muted">Riders Miami</small>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={limpiarFiltros}
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={cargarReporte}
                disabled={loading}
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={loading || filasFiltradas.length === 0}
              >
                PDF
              </Button>
            </div>
          </div>

          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} sm={6} lg>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Estado venta</Form.Label>
              <Form.Select
                value={estadoVenta}
                onChange={(e) => setEstadoVenta(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="PENDIENTE">PENDIENTE</option>
                <option value="CONFIRMADA">CONFIRMADA</option>
                <option value="EN_PROCESO">EN PROCESO</option>
                <option value="ENVIADA">ENVIADA</option>
                <option value="ENTREGADA">ENTREGADA</option>
                <option value="ANULADA">ANULADA</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Estado pago</Form.Label>
              <Form.Select
                value={estadoPago}
                onChange={(e) => setEstadoPago(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="PENDIENTE">PENDIENTE</option>
                <option value="PARCIAL">PARCIAL</option>
                <option value="PAGADO">PAGADO</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Medio pago</Form.Label>
              <Form.Select
                value={medioPago}
                onChange={(e) => setMedioPago(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="SIN PAGOS">SIN PAGOS</option>
                <option value="EFECTIVO EN TIENDA">EFECTIVO EN TIENDA</option>
                <option value="ZELLE">ZELLE</option>
                <option value="DEBITO">DEBITO</option>
                <option value="CREDITO">CREDITO</option>
                <option value="CREDITO FINANCIERA">CREDITO FINANCIERA</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Acreditación</Form.Label>
              <Form.Select
                value={estadoAcreditacion}
                onChange={(e) => setEstadoAcreditacion(e.target.value)}
                size="sm"
              >
                <option value="">Todas</option>
                <option value="PENDIENTE">PENDIENTE</option>
                <option value="ACREDITADO">ACREDITADO</option>
                <option value="ANULADO">ANULADO</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Financiera</Form.Label>
              <Form.Select
                value={financieraFiltro}
                onChange={(e) => setFinancieraFiltro(e.target.value)}
                size="sm"
              >
                <option value="">Todas</option>
                {financieras.map((f) => {
                  const id = f._id || f.id;
                  const nombre = f.financiera || f.razonsocial || id;

                  return (
                    <option key={id} value={id}>
                      {nombre}
                    </option>
                  );
                })}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Buscar</Form.Label>
              <Form.Control
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Venta, cliente, medio..."
                size="sm"
              />
            </Col>
          </Row>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          <Row className="g-2 mb-3">
            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Ventas</small>
                  <h5 className="mb-0">{totales.cantidadVentas}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Pagos</small>
                  <h5 className="mb-0">{totales.cantidadPagos}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Venta total</small>
                  <h5 className="mb-0">
                    {formatUSD(totales.totalVentasUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Tax</small>
                  <h5 className="mb-0 text-primary">
                    {formatUSD(totales.totalTaxUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Venta sin tax</small>
                  <h5 className="mb-0 text-primary">
                    {formatUSD(totales.totalSinTaxUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Cargado</small>
                  <h5 className="mb-0">{formatUSD(totales.totalCargadoUSD)}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Fee financiera</small>
                  <h5 className="mb-0 text-danger">
                    {formatUSD(totales.totalFeeUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Cobrado al cliente
                  </small>

                  <h5 className="mb-0 text-primary">
                    {formatUSD(
                      totales.totalCobradoClienteUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Monto financiera</small>
                  <h5 className="mb-0 text-success">
                    {formatUSD(totales.totalMontoFinancieraUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Acreditado</small>
                  <h5 className="mb-0">
                    {formatUSD(totales.totalAcreditadoUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Pend. acreditar</small>
                  <h5 className="mb-0">
                    {formatUSD(totales.totalPendienteAcreditarUSD)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Saldo</small>
                  <h5 className="mb-0">{formatUSD(totales.saldoPendienteUSD)}</h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="table-responsive" style={{ maxHeight: "65vh" }}>
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle reporte-financiero-table"
              style={{
                minWidth: "2335px",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "60px" }} />  {/* Fecha venta */}
                <col style={{ width: "30px" }} />  {/* Venta */}
                <col style={{ width: "190px" }} /> {/* Cliente */}
                <col style={{ width: "120px" }} /> {/* Vendedor */}
                <col style={{ width: "75px" }} />  {/* Estado venta */}
                <col style={{ width: "70px" }} />  {/* Estado pago */}

                <col style={{ width: "80px" }} /> {/* Venta total */}
                <col style={{ width: "80px" }} />  {/* Tax */}
                <col style={{ width: "80px" }} /> {/* Venta sin tax */}
                <col style={{ width: "80px" }} />  {/* Saldo */}

                <col style={{ width: "70px" }} />  {/* Fecha pago */}
                <col style={{ width: "120px" }} /> {/* Medio */}
                <col style={{ width: "105px" }} /> {/* Financiera */}
                <col style={{ width: "80px" }} /> {/* Monto aplicado */}
                <col style={{ width: "30px" }} />  {/* Fee % */}
                <col style={{ width: "80px" }} />  {/* Fee US$ */}
                <col style={{ width: "80px" }} /> {/* Total cliente */}
                <col style={{ width: "80px" }} /> {/* Monto financiera */}

                <col style={{ width: "70px" }} />  {/* Acreditación */}
                <col style={{ width: "40px" }} />  {/* Firmado */}
                <col style={{ width: "70px" }} />  {/* Fecha acreditación */}
                <col style={{ width: "160px" }} /> {/* Observaciones */}
              </colgroup>
            <thead
              className="table-dark"
              style={{ position: "sticky", top: 0, zIndex: 2 }}
            >
              <tr>
                <th>Fecha venta</th>
                <th>Venta</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th>Estado venta</th>
                <th>Estado pago</th>

                <th className="text-end">Venta total</th>
                <th className="text-end">Tax</th>
                <th className="text-end">Venta sin tax</th>
                <th className="text-end">Saldo</th>

                <th>Fecha pago</th>
                <th className="col-medio-pago">Medio</th>
                <th>Financiera</th>
                <th className="text-end">
                  Monto aplicado
                </th>

                <th className="text-end">Fee %</th>

                <th className="text-end">
                  Fee US$
                </th>

                <th className="text-end">
                  Total cliente
                </th>

                <th className="text-end">
                  Monto financiera
                </th>
                <th>Acreditación</th>
                <th>Firmado</th>
                <th>Fecha acred.</th>
                <th>Obs.</th>
              </tr>
            </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="22" className="text-center py-4">
                      <Spinner animation="border" size="sm" /> Cargando reporte...
                    </td>
                  </tr>
                ) : filasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan="22" className="text-center py-4 text-muted">
                      No hay datos financieros para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filasFiltradas.map((fila, index) => (
                    <tr
                      key={`${fila.ventaId}-${fila.pagoId || "sinpago"}-${index}`}
                    >
                      <td>{formatDate(fila.fechaVenta)}</td>

                      <td>{fila.nroventa}</td>

                      <td>
                        {normalizarTexto(
                          nombreClienteCompra(fila.clienteCompra)
                        )}
                      </td>

                      <td>
                        {normalizarTexto(fila.vendedorNombre)}
                      </td>

                      <td>{fila.estadoVenta}</td>

                      <td>
                        <span className={estadoPagoBadge(fila.estadoPago)}>
                          {fila.estadoPago}
                        </span>
                      </td>

                      {/* Bloque de venta */}
                      <td className="text-end fw-bold">
                        {formatUSD(fila.totalVentaUSD)}
                      </td>

                      <td className="text-end text-primary fw-bold">
                        {formatUSD(fila.taxMontoUSD)}
                      </td>

                      <td className="text-end text-primary fw-bold">
                        {formatUSD(fila.totalSinTaxUSD)}
                      </td>

                      <td className="text-end fw-bold">
                        {formatUSD(fila.saldoPendienteUSD)}
                      </td>

                      {/* Bloque de pagos */}
                      <td>{formatDate(fila.fechaPago)}</td>

                      <td className="col-medio-pago">
                        {fila.medioPago}
                      </td>

                      <td>{fila.financieraNombre}</td>

                      <td className="text-end fw-bold">
                        {formatUSD(fila.montoUSD)}
                      </td>

                      <td className="text-end">
                        {esPagoCreditoFinanciera(fila)
                          ? `${formatNum(fila.feePorcentaje)}%`
                          : "-"}
                      </td>

                      <td className="text-end text-danger fw-bold">
                        {esPagoCreditoFinanciera(fila)
                          ? formatUSD(fila.feeMontoUSD)
                          : "-"}
                      </td>

                      <td className="text-end text-primary fw-bold">
                        {formatUSD(
                          fila.totalCobrarClienteUSD
                        )}
                      </td>

                      <td className="text-end text-success fw-bold">
                        {esPagoCreditoFinanciera(fila)
                          ? formatUSD(fila.montoFinancieraUSD)
                          : "-"}
                      </td>

                      <td>
                        <span
                          className={estadoAcreditacionBadge(
                            fila.estadoAcreditacion
                          )}
                        >
                          {fila.estadoAcreditacion}
                        </span>
                      </td>

                      <td className="text-center">
                        <span className={contratoFirmadoBadge(fila)}>
                          {contratoFirmadoTexto(fila)}
                        </span>
                      </td>

                      <td>{formatDate(fila.fechaAcreditacion)}</td>

                      <td>{fila.observacionesPago || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {filasFiltradas.length > 0 && (
                <tfoot>
                  <tr className="reporte-financiero-total-row">
                    <th colSpan="6">Totales</th>

                    <th className="text-end">
                      {formatUSD(totales.totalVentasUSD)}
                    </th>

                    <th className="text-end text-primary">
                      {formatUSD(totales.totalTaxUSD)}
                    </th>

                    <th className="text-end text-primary">
                      {formatUSD(totales.totalSinTaxUSD)}
                    </th>

                    <th className="text-end">
                      {formatUSD(totales.saldoPendienteUSD)}
                    </th>

                    {/* Fecha, medio y financiera */}
                    <th colSpan="3"></th>

                    <th className="text-end">
                      {formatUSD(totales.totalCargadoUSD)}
                    </th>

                    {/* Fee porcentaje */}
                    <th></th>

                    <th className="text-end text-danger">
                      {formatUSD(totales.totalFeeUSD)}
                    </th>

                    <th className="text-end text-primary">
                      {formatUSD(
                        totales.totalCobradoClienteUSD
                      )}
                    </th>

                    <th className="text-end text-success">
                      {formatUSD(totales.totalMontoFinancieraUSD)}
                    </th>

                    <th className="text-end text-success">
                      {formatUSD(totales.totalAcreditadoUSD)}
                    </th>

                    {/* Firmado, fecha acreditación y observaciones */}
                    <th colSpan="3"></th>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}